import type { AspectRatio, Op, OpLog, Point, StrokeSnap } from './types.js';

/**
 * Stroke widths are stored against a reference panel this many CSS pixels wide.
 * A width of 4 is 4px on a 400px-wide panel and scales proportionally from there,
 * so lines look identical on a phone and on a 2x export.
 */
export const STROKE_REF_WIDTH = 400;

/** MVP pen. GUARDRAIL #2: these are defaults for new ops, never assumptions in renderers. */
export const DEFAULT_COLOR = '#000000';
export const DEFAULT_WIDTH = 4;

/**
 * The eraser is an ordinary stroke in the paper's colour (canvases are always
 * white, even in dark mode), so replay, undo and export need no special case.
 */
export const ERASER_COLOR = '#FFFFFF';
export const ERASER_WIDTH = DEFAULT_WIDTH * 2;

export const EMPTY_OPLOG: OpLog = { v: 1, ops: [] };

export const ASPECT_RATIOS: AspectRatio[] = ['1:2', '3:4', '1:1'];

/** width / height for a panel. '1:2' is a tall paper-proportioned panel. */
export function aspectValue(ratio: AspectRatio): number {
  const [w, h] = ratio.split(':').map(Number);
  return w / h;
}

export function isAspectRatio(v: unknown): v is AspectRatio {
  return typeof v === 'string' && (ASPECT_RATIOS as string[]).includes(v);
}

/* ------------------------------------------------------------- precision */

/**
 * GUARDRAIL #3 (as amended in Round 2.2): every captured point is kept, stored at
 * 4-decimal precision. 0.0001 of a panel is about 0.04px on a phone canvas and
 * 0.08px on a 2x PNG export: below input resolution, and it halves storage.
 * Timestamps are integers already. Points are never dropped, merged or thinned.
 */
export const COORD_DECIMALS = 4;
const COORD_SCALE = 10 ** COORD_DECIMALS;

/**
 * Round one coordinate (or pressure) to COORD_DECIMALS. Dividing an integer by
 * the scale gives the double nearest k/10000, which JSON prints with at most four
 * decimals; rounding an already-rounded value returns it unchanged.
 */
export function roundCoord(n: number): number {
  return Math.round(n * COORD_SCALE) / COORD_SCALE;
}

/** A point at storage precision: x, y (and pressure) rounded; t untouched. */
export function roundPoint(p: Point): Point {
  return p.length === 4
    ? [roundCoord(p[0]), roundCoord(p[1]), p[2], roundCoord(p[3])]
    : [roundCoord(p[0]), roundCoord(p[1]), p[2]];
}

/**
 * The op log at storage precision. Only coordinates change: op count, points per
 * op, every timestamp, style and `snap` are carried over exactly. Idempotent.
 */
export function roundOpLog(log: OpLog): OpLog {
  return {
    ...log,
    ops: log.ops.map((op) => (op.kind === 'stroke' ? { ...op, points: op.points.map(roundPoint) } : op)),
  };
}

/* ------------------------------------------------------------------ size */

/**
 * The largest op log a panel may have, as serialized JSON bytes. D1 refuses any
 * row over 2 MB; this leaves 500 KB of margin for the row's other columns (a few
 * hundred bytes in practice). The server validator and the drawing screen use
 * this same number, so a panel the client lets you draw is always storable.
 */
export const MAX_OPLOG_BYTES = 1_500_000;

/** The drawing screen's gentle "getting very detailed" note appears at this share of the limit. */
export const OPLOG_WARN_RATIO = 0.75;

/**
 * Serialized size of an op log: exactly what gets stored. Op logs are ASCII
 * (numbers, hex colours, fixed keys), so string length is byte length.
 */
export function opLogBytes(log: OpLog): number {
  return JSON.stringify(log).length;
}

/*
 * Structural caps, derived from the byte limit so they can never reject a log
 * that fits: the smallest possible point, `[0,0,0],`, is 8 bytes, and the smallest
 * op is well over 40. They only exist to refuse absurd input before serializing it.
 */
const MIN_POINT_BYTES = 8;
const MIN_OP_BYTES = 40;
export const MAX_TOTAL_POINTS = Math.floor(MAX_OPLOG_BYTES / MIN_POINT_BYTES);
export const MAX_POINTS_PER_OP = MAX_TOTAL_POINTS;
export const MAX_OPS = Math.floor(MAX_OPLOG_BYTES / MIN_OP_BYTES);

/* ------------------------------------------------------------ validation */

export class OpValidationError extends Error {
  constructor(
    message: string,
    /** Machine-readable reason; the API returns it as the error code. */
    readonly code: 'invalid_ops' | 'panel_too_large' = 'invalid_ops',
  ) {
    super(message);
  }
}

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

function num(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/**
 * Parse and normalize an op log arriving from a client. Throws OpValidationError
 * on anything malformed. Returns a clean structure safe to persist.
 *
 * Coordinates are clamped to 0..1 rather than rejected — a stray pointer sample a
 * hair outside the canvas should not cost someone their drawing (guardrail #7).
 */
export function validateOpLog(input: unknown): OpLog {
  if (!input || typeof input !== 'object') throw new OpValidationError('ops must be an object');
  const raw = input as Record<string, unknown>;
  if (raw.v !== 1) throw new OpValidationError('unsupported op log version');
  if (!Array.isArray(raw.ops)) throw new OpValidationError('ops.ops must be an array');
  if (raw.ops.length > MAX_OPS) throw new OpValidationError('This panel is too detailed to save (too many strokes)', 'panel_too_large');

  let totalPoints = 0;
  const ops: Op[] = raw.ops.map((o, i) => {
    if (!o || typeof o !== 'object') throw new OpValidationError(`op ${i} is not an object`);
    const op = o as Record<string, unknown>;
    // Dispatch over `kind` even with one case — future kinds slot in here.
    switch (op.kind) {
      case 'stroke': {
        if (typeof op.color !== 'string' || !HEX_COLOR.test(op.color)) {
          throw new OpValidationError(`op ${i} has an invalid color`);
        }
        if (!num(op.width) || op.width <= 0 || op.width > 200) {
          throw new OpValidationError(`op ${i} has an invalid width`);
        }
        if (!Array.isArray(op.points)) throw new OpValidationError(`op ${i} has no points`);
        if (op.points.length > MAX_POINTS_PER_OP) throw new OpValidationError('This panel is too detailed to save (a stroke has too many points)', 'panel_too_large');
        totalPoints += op.points.length;
        if (totalPoints > MAX_TOTAL_POINTS) throw new OpValidationError('This panel is too detailed to save (too many points)', 'panel_too_large');
        const points: Point[] = op.points.map((p, j) => {
          if (!Array.isArray(p) || p.length < 3 || p.length > 4) {
            throw new OpValidationError(`op ${i} point ${j} is malformed`);
          }
          if (!p.every(num)) throw new OpValidationError(`op ${i} point ${j} is not numeric`);
          const [x, y, t, pressure] = p as number[];
          // Normalized to storage precision whatever client sent it, so every stored
          // panel follows one format. Pressure is kept when present (the renderer
          // ignores it for now).
          return roundPoint(
            p.length === 4
              ? [clamp01(x), clamp01(y), Math.max(0, t), clamp01(pressure)]
              : [clamp01(x), clamp01(y), Math.max(0, t)],
          );
        });
        const stroke: Op = { kind: 'stroke', color: op.color, width: op.width, points };
        if (op.snap !== undefined && op.snap !== null) stroke.snap = validateSnap(op.snap, points.length, i);
        return stroke;
      }
      default:
        throw new OpValidationError(`op ${i} has unknown kind`);
    }
  });

  const log: OpLog = { v: 1, ops };
  const bytes = opLogBytes(log);
  if (bytes > MAX_OPLOG_BYTES) {
    throw new OpValidationError(
      `This panel is too detailed to save (${(bytes / 1e6).toFixed(2)} MB; the limit is ${MAX_OPLOG_BYTES / 1e6} MB)`,
      'panel_too_large',
    );
  }
  return log;
}

function validateSnap(raw: unknown, pointCount: number, i: number): StrokeSnap {
  const snap = raw as Record<string, unknown>;
  if (!snap || typeof snap !== 'object' || snap.kind !== 'line') {
    throw new OpValidationError(`op ${i} has an unknown snap`);
  }
  const at = snap.at;
  if (typeof at !== 'number' || !Number.isInteger(at) || at < 0 || at >= pointCount) {
    throw new OpValidationError(`op ${i} has a snap outside its points`);
  }
  return { kind: 'line', at };
}

/** Tolerant read of an op log out of the database. Never throws on legacy/NULL rows. */
export function parseOpLog(json: string | null | undefined): OpLog | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.ops)) return null;
    return { v: 1, ops: parsed.ops as Op[] };
  } catch {
    return null;
  }
}

export function countPoints(log: OpLog | null): number {
  if (!log) return 0;
  let n = 0;
  for (const op of log.ops) if (op.kind === 'stroke') n += op.points.length;
  return n;
}
